<template>
<div class="api-failure-log-page">
  <header class="page-heading">
    <div>
      <span class="eyebrow">OPERATIONS / API</span>
      <h1>{{ $t('接口错误日志') }}</h1>
      <p>{{ $t('查看上游接口调用失败记录、返回码以及完整请求和响应，便于定位异常。') }}</p>
    </div>
    <el-button icon="el-icon-back" @click="$router.push('/home')">{{ $t('返回首页') }}</el-button>
  </header>

  <section class="log-panel">
    <el-form :inline="true" :model="filters" class="filter-form" @submit.native.prevent>
      <el-form-item :label="$t('时间范围')">
        <el-select v-model="filters.hours" class="period-select" @change="search">
          <el-option :label="$t('近24小时')" :value="24" />
          <el-option :label="$t('近3天')" :value="72" />
          <el-option :label="$t('近7天')" :value="168" />
          <el-option :label="$t('近30天')" :value="720" />
        </el-select>
      </el-form-item>
      <el-form-item :label="$t('接口动作')">
        <el-input v-model.trim="filters.action" clearable :placeholder="$t('如 CREATE_ORDER')" @keyup.enter.native="search" />
      </el-form-item>
      <el-form-item :label="$t('返回码')">
        <el-input v-model.trim="filters.resultCode" clearable :placeholder="$t('上游返回码')" @keyup.enter.native="search" />
      </el-form-item>
      <el-form-item :label="$t('客户')">
        <el-input v-model.trim="filters.customerName" clearable :placeholder="$t('账号或名称')" @keyup.enter.native="search" />
      </el-form-item>
      <el-form-item>
        <el-button type="primary" icon="el-icon-search" @click="search">{{ $t('查询') }}</el-button>
        <el-button icon="el-icon-refresh-left" @click="reset">{{ $t('重置') }}</el-button>
      </el-form-item>
    </el-form>

    <div class="result-summary">
      <span><i class="el-icon-warning-outline" /> {{ $t('当前范围共') }} <strong>{{ total }}</strong> {{ $t('条失败记录') }}</span>
      <small>{{ $t('这里只统计上游接口调用日志，不等同于“预报失败订单”数量。') }}</small>
    </div>

    <el-table v-loading="loading" :data="rows" border stripe :empty-text="$t('当前筛选范围没有接口失败记录')">
      <el-table-column prop="createdAt" :label="$t('发生时间')" width="170">
        <template slot-scope="scope">{{ formatDate(scope.row.createdAt) }}</template>
      </el-table-column>
      <el-table-column :label="$t('接口动作')" min-width="170">
        <template slot-scope="scope">
          <strong class="action-name">{{ apiActionText(scope.row.action) }}</strong>
          <small class="action-code">{{ scope.row.action || '-' }}</small>
        </template>
      </el-table-column>
      <el-table-column prop="customerName" :label="$t('客户/触发方')" min-width="140">
        <template slot-scope="scope">{{ scope.row.customerName || $t('系统任务') }}</template>
      </el-table-column>
      <el-table-column :label="$t('返回码')" width="115" align="center">
        <template slot-scope="scope"><el-tag size="small" type="danger">{{ scope.row.resultCode || $t('失败') }}</el-tag></template>
      </el-table-column>
      <el-table-column prop="failureReason" :label="$t('失败原因')" min-width="320" show-overflow-tooltip />
      <el-table-column :label="$t('操作')" width="90" fixed="right" align="center">
        <template slot-scope="scope"><el-button type="text" @click="openDetail(scope.row)">{{ $t('查看详情') }}</el-button></template>
      </el-table-column>
    </el-table>

    <div class="pagination-wrap">
      <el-pagination
        background
        layout="total, sizes, prev, pager, next, jumper"
        :current-page="pageNum"
        :page-size="pageSize"
        :page-sizes="[10, 20, 50, 100]"
        :total="total"
        @size-change="changePageSize"
        @current-change="changePage"
      />
    </div>
  </section>

  <el-dialog :title="$t('接口错误详情')" :visible.sync="detailVisible" width="760px" top="6vh" append-to-body>
    <div v-loading="detailLoading" class="detail-body">
      <template v-if="detail">
        <div class="detail-summary-grid">
          <div><span>{{ $t('发生时间') }}</span><strong>{{ formatDate(detail.createdAt) }}</strong></div>
          <div><span>{{ $t('接口动作') }}</span><strong>{{ apiActionText(detail.action) }}（{{ detail.action || '-' }}）</strong></div>
          <div><span>{{ $t('客户/触发方') }}</span><strong>{{ detail.customerName || $t('系统任务') }}</strong></div>
          <div><span>{{ $t('返回码') }}</span><strong class="error-code">{{ detail.resultCode || $t('失败') }}</strong></div>
        </div>
        <div class="failure-reason"><span>{{ $t('失败原因') }}</span><p>{{ detail.failureReason || '-' }}</p></div>
        <div class="json-section"><h3>{{ $t('请求内容') }}</h3><pre>{{ formatJson(detail.requestJson) }}</pre></div>
        <div class="json-section"><h3>{{ $t('响应内容') }}</h3><pre>{{ formatJson(detail.responseJson) }}</pre></div>
      </template>
    </div>
    <span slot="footer"><el-button @click="detailVisible = false">{{ $t('关闭') }}</el-button></span>
  </el-dialog>
</div>
</template>

<script>
import { translateUi } from '../../i18n'
import { getApiFailureLog, listApiFailureLogs } from '@/api/transport'

export default {
  name: 'TransportApiFailureLogs',
  data () {
    const routeHours = Number(this.$route.query.hours)
    return {
      loading: false,
      rows: [],
      total: 0,
      pageNum: 1,
      pageSize: 20,
      filters: {
        hours: [24, 72, 168, 720].includes(routeHours) ? routeHours : 24,
        action: '',
        resultCode: '',
        customerName: ''
      },
      detailVisible: false,
      detailLoading: false,
      detail: null
    }
  },
  created () {
    this.loadRows()
  },
  methods: {
    loadRows () {
      this.loading = true
      return listApiFailureLogs({ ...this.filters, pageNum: this.pageNum, pageSize: this.pageSize })
        .then(data => {
          this.rows = data.list || []
          this.total = Number(data.total || 0)
        })
        .catch(() => {
          this.rows = []
          this.total = 0
        })
        .finally(() => { this.loading = false })
    },
    search () {
      this.pageNum = 1
      this.$router.replace({ path: this.$route.path, query: { hours: this.filters.hours } }).catch(() => {})
      this.loadRows()
    },
    reset () {
      this.filters = { hours: 24, action: '', resultCode: '', customerName: '' }
      this.search()
    },
    changePageSize (value) {
      this.pageSize = value
      this.pageNum = 1
      this.loadRows()
    },
    changePage (value) {
      this.pageNum = value
      this.loadRows()
    },
    openDetail (row) {
      this.detailVisible = true
      this.detailLoading = true
      this.detail = null
      getApiFailureLog(row.id)
        .then(data => { this.detail = data })
        .catch(() => { this.detailVisible = false })
        .finally(() => { this.detailLoading = false })
    },
    formatDate (value) {
      return value ? String(value).replace('T', ' ').slice(0, 19) : '-'
    },
    formatJson (value) {
      if (value === null || value === undefined || value === '') return '无内容'
      try {
        return JSON.stringify(typeof value === 'string' ? JSON.parse(value) : value, null, 2)
      } catch (error) {
        return String(value)
      }
    },
    apiActionText (action) {
      const labels = {
        PRODUCT_SYNC: translateUi('同步产品'),
        QUOTE: translateUi('运费试算'),
        QUOTE_BEFORE_ORDER: translateUi('下单前复核'),
        CREATE_ORDER: translateUi('创建订单'),
        ORDER_STATUS: translateUi('订单状态'),
        ORDER_DETAIL: translateUi('订单详情'),
        ORDER_DETAIL_REVIEW: translateUi('订单详情核对'),
        ORDER_DETAIL_UPDATE: translateUi('订单详情更新'),
        GET_LABEL: translateUi('获取面单'),
        ORDER_TRACE: translateUi('运单轨迹'),
        ORDER_WAYBILL: translateUi('查询运单号'),
        ORDER_DELETE: translateUi('删除订单'),
        ORDER_DELETE_CHECK: translateUi('删除状态核对'),
        ORDER_RESTORE_CHECK: translateUi('恢复状态核对'),
        ORDER_PIECES: translateUi('获取包裹信息'),
        ORDER_PIECES_DETAIL: translateUi('获取包裹详情'),
        ORDER_DETAIL_VIEW: translateUi('查看订单详情')
      }
      return labels[action] || action || '上游接口'
    }
  }
}
</script>

<style scoped>
.api-failure-log-page { min-height: calc(100vh - 84px); padding: 24px; background: #f4f7f6; }
.page-heading { display: flex; align-items: flex-start; justify-content: space-between; gap: 20px; max-width: 1500px; margin: 0 auto 18px; }
.page-heading h1 { margin: 5px 0 7px; color: #243735; font-size: 25px; }
.page-heading p { margin: 0; color: #71817d; font-size: 13px; }
.eyebrow { color: #268878; font-size: 11px; font-weight: 700; letter-spacing: .12em; }
.log-panel { max-width: 1500px; margin: 0 auto; padding: 20px; border: 1px solid #e1e9e6; border-radius: 7px; background: #fff; box-shadow: 0 3px 12px rgba(31, 76, 69, .04); }
.filter-form { margin-bottom: 4px; }
.filter-form .el-form-item { margin-bottom: 14px; }
.period-select { width: 130px; }
.result-summary { display: flex; align-items: center; justify-content: space-between; gap: 20px; margin-bottom: 14px; padding: 10px 13px; color: #536762; background: #f5f9f8; border-radius: 5px; font-size: 13px; }
.result-summary i { margin-right: 5px; color: #d85d54; }
.result-summary strong { color: #c54942; }
.result-summary small { color: #879590; }
.action-name, .action-code { display: block; }
.action-name { color: #2d433f; }
.action-code { margin-top: 3px; color: #95a19e; font-size: 11px; }
.pagination-wrap { display: flex; justify-content: flex-end; padding-top: 18px; }
.detail-body { min-height: 180px; }
.detail-summary-grid { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 10px; }
.detail-summary-grid > div, .failure-reason { padding: 11px 13px; border: 1px solid #e6ecea; border-radius: 5px; background: #f8faf9; }
.detail-summary-grid span, .failure-reason > span { display: block; margin-bottom: 5px; color: #879590; font-size: 11px; }
.detail-summary-grid strong { color: #304641; font-size: 13px; word-break: break-all; }
.detail-summary-grid .error-code { color: #c54942; }
.failure-reason { margin-top: 10px; }
.failure-reason p { margin: 0; color: #9f3f38; line-height: 1.6; }
.json-section { margin-top: 16px; }
.json-section h3 { margin: 0 0 8px; color: #344a45; font-size: 14px; }
.json-section pre { box-sizing: border-box; max-height: 280px; margin: 0; padding: 13px; overflow: auto; border-radius: 5px; color: #d9e7e3; background: #263a36; font: 12px/1.6 Consolas, Monaco, monospace; white-space: pre-wrap; word-break: break-all; }
@media (max-width: 760px) {
  .api-failure-log-page { padding: 14px; }
  .page-heading { align-items: center; }
  .page-heading p { display: none; }
  .log-panel { padding: 14px; }
  .result-summary { align-items: flex-start; flex-direction: column; gap: 5px; }
  .detail-summary-grid { grid-template-columns: 1fr; }
}
</style>
